import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AdminLabel, LabelProduct, ProductService } from '../../_services/product.service';
import { AlertService } from '../../_services/alert.service';
import { UserService } from '../../_services/users.service';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { canDeleteDeliveryTerms } from '../delivery-terms/delivery-terms.component';
import { LABEL_STYLE_NAMES, labelOrder, labelStyle } from './label-style';
import { LabelPreviewComponent } from './label-preview.component';

type Filter = 'ALL' | 'USED' | 'UNUSED';
// Product labels ("NEW ARRIVAL", "Stock Clearance"...) shown on product
// images: a searchable list with how many products carry each, a preview of
// how it looks, and a view panel listing those products. Creating and editing
// happen on their own page. Admins can delete; a label on products is only
// deleted once they confirm taking it off those products too.
@Component({
    selector: 'app-labels',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, LabelPreviewComponent],
    templateUrl: './labels.component.html',
    styles: [`
    .lb-fade { animation: lb-fade .18s ease-out; }
    .lb-slide { animation: lb-slide .24s cubic-bezier(.2,.8,.2,1); }
    @keyframes lb-fade { from { opacity: 0; } }
    @keyframes lb-slide { from { transform: translateX(100%); } }
  `]
})
export class LabelsComponent implements OnInit {
  private productService = inject(ProductService);
  private alertService = inject(AlertService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly labelStyle = labelStyle;
  readonly styleNames = LABEL_STYLE_NAMES;
  readonly Math = Math;
  readonly canDelete = canDeleteDeliveryTerms(this.userService.isLoggedIn());

  loading = true;
  loadError = false;
  labels: AdminLabel[] = [];
  search = '';
  filter: Filter = 'ALL';

  selected: AdminLabel | null = null;
  products: LabelProduct[] = [];
  productsLoading = false;
  productSearch = '';

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  ngOnInit(): void {
    this.load(Number(this.route.snapshot.queryParamMap.get('selected')) || null);
  }

  load(openId: number | null = null): void {
    this.loading = true;
    this.loadError = false;
    this.productService.getAdminLabels().subscribe({
      next: (rows) => {
        this.labels = rows;
        this.loading = false;
        if (this.selected) this.selected = rows.find((r) => r.id === this.selected!.id) ?? null;
        const open = openId ? rows.find((r) => r.id === openId) : null;
        if (open) this.view(open);
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  get counts() {
    const used = this.labels.filter((l) => l.products > 0).length;
    return { total: this.labels.length, used, unused: this.labels.length - used, onProducts: this.labels.reduce((n, l) => n + l.products, 0) };
  }

  // Most-used first, then by name.
  get visible(): AdminLabel[] {
    const q = this.search.trim().toLowerCase();
    return this.labels
      .filter((l) => this.filter === 'ALL' || (this.filter === 'USED' ? l.products > 0 : l.products === 0))
      .filter((l) => !q || l.title.toLowerCase().includes(q) || l.label.toLowerCase().includes(q))
      .sort(labelOrder);
  }

  get groups() {
    const all = this.visible;
    return [
      { key: 'USED', title: 'On products', hint: 'Showing on the website now', icon: 'sell', items: all.filter((l) => l.products > 0) },
      { key: 'UNUSED', title: 'Not on any product', hint: 'Ready to add from a product\'s General tab', icon: 'label_off', items: all.filter((l) => l.products === 0) },
    ].filter((g) => g.items.length);
  }

  get maxProducts(): number {
    return Math.max(1, ...this.labels.map((l) => l.products));
  }

  // ---- View ----
  view(l: AdminLabel): void {
    if (this.selected?.id === l.id) {
      this.selected = null;
      return;
    }
    this.selected = l;
    this.products = [];
    this.productSearch = '';
    if (!l.products) return;
    this.productsLoading = true;
    this.productService.getLabelProducts(l.id).subscribe({
      next: (rows) => {
        this.products = rows;
        this.productsLoading = false;
      },
      error: () => {
        this.productsLoading = false;
        this.alertService.alert('Could not load the products with this label', 'error');
      },
    });
  }

  get visibleProducts(): LabelProduct[] {
    const q = this.productSearch.trim().toLowerCase();
    return q ? this.products.filter((p) => p.productName.toLowerCase().includes(q)) : this.products;
  }

  openCreate(): void {
    this.router.navigate(['/labels/create']);
  }

  openEdit(l: AdminLabel, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/labels/edit', l.id]);
  }

  remove(l: AdminLabel, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDelete) return;
    const n = l.products;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete label',
          message: `Delete "${l.title}"?`,
          detail: n ? `It's on ${n} product${n === 1 ? '' : 's'} - it will be taken off ${n === 1 ? 'it' : 'them'} too.` : "No products carry it. This can't be undone.",
          confirmText: 'Delete label',
          tone: 'danger',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.productService.deleteLabel(l.id, n > 0).subscribe({
          next: (r) => {
            this.alertService.alert(r.removedFrom ? `Label deleted and taken off ${r.removedFrom} product${r.removedFrom === 1 ? '' : 's'}` : 'Label deleted', 'success');
            if (this.selected?.id === l.id) this.selected = null;
            this.load();
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the label', 'error'),
        });
      });
  }
}
